/**
 * trackPageview.ts
 *
 * Call trackPageview() on every page. Sends to `${apiUrl}/home`.
 *
 * npm install ua-parser-js
 */
import axios from 'axios';
import { UAParser } from 'ua-parser-js';
import { getApiUrl } from './config';

const VISITOR_STORAGE_KEY = 'analytics_visitor_id';

// set after each successful trackPageview() call; used to close out the
// last page's dwell time when the visitor leaves the site.
let currentSessionId: string | null = null;
let lastPageViewedAt: number | null = null;

function generateUUID(): string {
  // crypto.randomUUID only exists in secure contexts (HTTPS or the literal
  // hostname "localhost") — fall back to a manual UUID v4 elsewhere (e.g.
  // CI runners that serve the app from a non-localhost hostname over HTTP).
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function getOrCreateVisitorId(): string {
  try {
    let visitorId = localStorage.getItem(VISITOR_STORAGE_KEY);
    if (!visitorId) {
      visitorId = generateUUID();
      localStorage.setItem(VISITOR_STORAGE_KEY, visitorId);
    }
    return visitorId;
  } catch {
    return generateUUID();
  }
}

function getTrafficInfo(): {
  trafficSource: string | null;
  trafficMedium: string | null;
  trafficCampaign: string | null;
} {
  const params = new URLSearchParams(window.location.search);
  const utmSource = params.get('utm_source');
  const utmMedium = params.get('utm_medium');
  const utmCampaign = params.get('utm_campaign');

  if (utmSource || utmMedium) {
    return { trafficSource: utmSource, trafficMedium: utmMedium, trafficCampaign: utmCampaign };
  }

  const referrer = document.referrer;
  if (!referrer) {
    return { trafficSource: 'direct', trafficMedium: 'direct', trafficCampaign: null };
  }

  let referrerHost: string;
  try {
    referrerHost = new URL(referrer).hostname.replace(/^www\./, '');
  } catch {
    return { trafficSource: null, trafficMedium: 'referral', trafficCampaign: null };
  }

  if (referrerHost === window.location.hostname) {
    return { trafficSource: null, trafficMedium: null, trafficCampaign: null };
  }

  const searchEngines = ['google.', 'bing.', 'yahoo.', 'duckduckgo.'];
  const socialSites = ['facebook.', 'twitter.', 'x.com', 'linkedin.', 'instagram.'];

  if (searchEngines.some((s) => referrerHost.includes(s))) {
    return { trafficSource: referrerHost, trafficMedium: 'organic', trafficCampaign: null };
  }
  if (socialSites.some((s) => referrerHost.includes(s))) {
    return { trafficSource: referrerHost, trafficMedium: 'social', trafficCampaign: null };
  }
  return { trafficSource: referrerHost, trafficMedium: 'referral', trafficCampaign: null };
}

const REQUEST_TIMEOUT_MS = 3000;

/**
 * Fire-and-forget: never throws, never awaited by the caller, and bails
 * out fast (REQUEST_TIMEOUT_MS) instead of hanging if the API host is
 * unreachable — matters on locked-down networks (e.g. CI runners with
 * restricted internet access) where a dead connection can otherwise hang
 * far longer than on a normal network.
 */
export function trackPageview(): void {
  // setTimeout(..., 0) pushes this to a macrotask so it can never run
  // inside whatever render/paint cycle triggered it — guarantees this
  // can't be part of anything the page/browser considers "loading".
  setTimeout(() => {
    void trackPageviewAsync();
  }, 0);
}

async function trackPageviewAsync(): Promise<void> {
  try {
    const apiUrl = getApiUrl(); // e.g. https://webapitest.icegems.com/api
    const ua = new UAParser().getResult();
    const traffic = getTrafficInfo();

    const payload = {
      visitorId: getOrCreateVisitorId(),
      pageUrl: window.location.href,
      pageTitle: document.title,
      referrerUrl: document.referrer || null,
      trafficSource: traffic.trafficSource,
      trafficMedium: traffic.trafficMedium,
      trafficCampaign: traffic.trafficCampaign,
      browser: ua.browser.name ?? null,
      browserVersion: ua.browser.version ?? null,
      operatingSystem: ua.os.name ?? null,
      osVersion: ua.os.version ?? null,
      deviceType:
        ua.device.type === 'mobile' || ua.device.type === 'tablet' ? ua.device.type : 'desktop',
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      country: null,
      region: null,
    };

    const response = await axios.post(`${apiUrl}/home`, payload, { timeout: REQUEST_TIMEOUT_MS });
    currentSessionId = response.data.sessionId;
    lastPageViewedAt = Date.now();
  } catch (error) {
    // Never let a tracking failure surface as an uncaught error/rejection —
    // this must never affect the page it's running on.
    console.error('Pageview tracking failed:', error);
  }
}

// ---------------------------------------------------------------------
// End-of-session beacon: fired when the visitor actually leaves the site
// (tab hidden/closed, navigating away). sendBeacon fires reliably even
// as the page is unloading, unlike a normal axios call.
// ---------------------------------------------------------------------
function endCurrentSession(): void {
  try {
    if (!currentSessionId || !navigator.sendBeacon || lastPageViewedAt === null) {
      return;
    }
    const apiUrl = getApiUrl();
    const lastPageSeconds = Math.round((Date.now() - lastPageViewedAt) / 1000);
    // Sent as query params (no body) to sidestep body-parser ordering issues
    // elsewhere in the app — req.query works regardless of that.
    const url =
      `${apiUrl}/home/end-session` +
      `?sessionId=${encodeURIComponent(currentSessionId)}` +
      `&lastPageSeconds=${lastPageSeconds}`;
    navigator.sendBeacon(url);
  } catch (error) {
    console.error('End-session beacon failed:', error);
  }
}

// Registered once, when this module first loads (RouteChangeTracker only
// imports it once for the life of the app). Wrapped in try/catch so a
// missing/nonstandard API in an unusual test environment can't crash the
// app just from importing this module.
try {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      endCurrentSession();
    }
  });
  window.addEventListener('pagehide', endCurrentSession);
} catch (error) {
  console.error('Failed to register end-session listeners:', error);
}